import React from 'react';

import { Stack, Text } from '@eluve/components';

export type AffectedUserPreviewRow = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  specialty: string | null;
  currentBundleName?: string | null;
};

interface AffectedUsersPreviewProps {
  users: AffectedUserPreviewRow[];
  emptyMessage: string;
  /** When true, render the per-row "current default bundle" column. */
  showCurrentBundle?: boolean;
}

const SPECIALTY_NONE = '(no specialty)';
const BUNDLE_NONE = '(no default)';

export const AffectedUsersPreview: React.FC<AffectedUsersPreviewProps> = ({
  users,
  emptyMessage,
  showCurrentBundle = false,
}) => {
  if (users.length === 0) {
    return (
      <Stack className="rounded-md border border-border-secondary bg-bg-secondary p-3">
        <Text color="supporting">{emptyMessage}</Text>
      </Stack>
    );
  }

  return (
    <Stack
      gap={2}
      className="max-h-64 overflow-y-auto rounded-md border border-border-secondary bg-bg-secondary p-3"
    >
      <Text size="xs" weight="semibold" color="supporting">
        {users.length} user{users.length === 1 ? '' : 's'} affected
      </Text>
      <Stack gap={1}>
        {users.map((u) => (
          <Stack
            key={u.userId}
            direction="horizontal"
            gap={2}
            justify="between"
            align="start"
          >
            <Stack gap={0}>
              <Text inline>
                {[u.firstName, u.lastName].filter(Boolean).join(' ') ||
                  '(no name)'}
              </Text>
              <Text inline size="xs" color="supporting">
                {u.email ?? '(no email)'}
              </Text>
            </Stack>
            <Stack gap={0} align="end">
              <Text inline size="xs" color="supporting">
                {u.specialty?.trim() || SPECIALTY_NONE}
              </Text>
              {showCurrentBundle && (
                <Text inline size="xs" color="supporting">
                  {u.currentBundleName ?? BUNDLE_NONE}
                </Text>
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
